import { BOOptionModel } from "../data-model/boOptionModel";

/**
 * Result of validating report statuses against BO options.
 * @interface ValidateBoStatusResult
 * @property {boolean} isValid - Whether all statuses are valid.
 * @property {string[]} invalidStatuses - Array of invalid status values found.
 */
export interface ValidateBoStatusResult {
  isValid: boolean;
  invalidStatuses: string[];
}

/**
 * Validates that all provided report status values exist in the BO options.
 *
 * @param {BOOptionModel[]} boOptions - The BO options from Redux store.
 * @param {string[]} reportStatuses - Array of report status values to validate.
 * @returns {ValidateBoStatusResult} Result object with validation status and any invalid values.
 * @example
 * const result = validateReportStatusesAgainstBoOptions(boOptions, ["approved", "pending"]);
 * if (!result.isValid) {
 *   console.error("Invalid statuses:", result.invalidStatuses);
 * }
 */
export const validateReportStatusesAgainstBoOptions = (
  boOptions: BOOptionModel[],
  reportStatuses: string[],
): ValidateBoStatusResult => {
  if (reportStatuses.length === 0) {
    return { isValid: true, invalidStatuses: [] };
  }

  const validBoValues = new Set(boOptions.map((option) => option.value));
  const invalidStatuses = [
    ...new Set(
      reportStatuses.filter((status) => status && !validBoValues.has(status)),
    ),
  ];
  return {
    isValid: invalidStatuses.length === 0,
    invalidStatuses,
  };
};
